import Box from '@mui/material/Box';
import { MessagingChannelType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useState } from 'react';

import UnreadCountBadge from '~components/Messaging/UnreadCountBadge';
import StatusTabs, { StatusTab } from '~components/Tabs/StatusTabs';
import TabLabelWithAdornment from '~components/Tabs/TabLabelWithAdornment';
import { Order } from '~hooks/useOrders/models';
import OrderJobConversations from '~pages/Dispatch/components/OrderMessaging/OrderJobConversations';
import OrderThreadPanel from '~pages/Dispatch/components/OrderMessaging/OrderThreadPanel';
import useMessagingChannelUnreadCountQuery from '~queries/useMessagingChannelUnreadCountQuery';
import useMessagingOrderUnreadCountQuery from '~queries/useMessagingOrderUnreadCountQuery';
import useMessagingTargetChannelQuery from '~queries/useMessagingTargetChannelQuery';

enum OrderMessagingSegment {
  ORDER_THREAD = 'order-thread',
  JOB_DMS = 'job-dms',
}

type OrderMessagingTabProps = {
  order: Order;
  readOnly?: boolean;
};

export default function OrderMessagingTab({ order, readOnly }: OrderMessagingTabProps) {
  const [selectedSegment, setSelectedSegment] = useState(
    OrderMessagingSegment.ORDER_THREAD,
  );
  const {
    channel: orderChannel,
    isError: isOrderChannelLookupError,
    isLoading: isOrderChannelLoading,
  } = useMessagingTargetChannelQuery({
    channelType: MessagingChannelType.ORDER,
    targetId: order.id,
  });
  const { unreadCount: orderThreadUnreadCount } = useMessagingChannelUnreadCountQuery(
    orderChannel?.id,
  );
  const { unreadCount: orderUnreadCount } = useMessagingOrderUnreadCountQuery(order);
  const jobDmsUnreadCount = orderUnreadCount - orderThreadUnreadCount;
  const isOrderChannelUnresolved = isOrderChannelLookupError && !orderChannel;
  const areSegmentCountsKnown = !isOrderChannelLoading && !isOrderChannelUnresolved;
  const segmentBadge = (count: number) =>
    areSegmentCountsKnown ? <UnreadCountBadge count={count} /> : null;

  const segmentTabs: StatusTab<OrderMessagingSegment>[] = [
    {
      label: (
        <TabLabelWithAdornment
          endAdornment={segmentBadge(orderThreadUnreadCount)}
          label={t('messaging.order_tab.sub_tabs.order_thread')}
        />
      ),
      value: OrderMessagingSegment.ORDER_THREAD,
    },
    {
      label: (
        <TabLabelWithAdornment
          endAdornment={segmentBadge(jobDmsUnreadCount)}
          label={t('messaging.order_tab.sub_tabs.job_dms')}
        />
      ),
      value: OrderMessagingSegment.JOB_DMS,
    },
  ];

  return (
    <Box>
      <StatusTabs
        onChange={setSelectedSegment}
        selectedTab={selectedSegment}
        sx={{ width: 'fit-content' }}
        tabs={segmentTabs}
      />

      <Box sx={{ pt: 1.5 }}>
        {selectedSegment === OrderMessagingSegment.ORDER_THREAD ? (
          <OrderThreadPanel order={order} readOnly={readOnly} />
        ) : (
          <OrderJobConversations order={order} readOnly={readOnly} />
        )}
      </Box>
    </Box>
  );
}
